/**
 * Smart Time Parser Utilities
 *
 * Parses freeform time input and intelligently determines AM/PM
 * based on typical shift hours (9am - 8pm).
 */

export interface ParsedTime {
  hours: number;      // 0-23 (24-hour format)
  minutes: number;    // 0-59
  period: 'AM' | 'PM';
  display: string;    // Formatted display string (e.g., "9:30 AM")
  time24: string;     // 24-hour format for API (e.g., "09:30")
  valid: boolean;
  error?: string;
}

/**
 * Detects AM or PM based on the hour and typical shift hours.
 *
 * Logic:
 * - Hours 9-11 → AM (morning shift)
 * - Hour 12 → PM (noon)
 * - Hours 1-8 → PM (afternoon/evening shift)
 *
 * @param hour - The hour in 12-hour format (1-12)
 * @returns 'AM' or 'PM'
 */
export function detectPeriod(hour: number): 'AM' | 'PM' {
  // Hour 12 is always PM (noon)
  if (hour === 12) return 'PM';

  // Hours 9-11 are AM (morning shift)
  if (hour >= 9 && hour <= 11) return 'AM';

  // Hours 1-8 are PM (afternoon/evening shift)
  if (hour >= 1 && hour <= 8) return 'PM';

  // Fallback (shouldn't reach here for valid 1-12 hours)
  return 'AM';
}

/**
 * Converts 12-hour time to 24-hour format.
 *
 * @param hour - Hour in 12-hour format (1-12)
 * @param period - 'AM' or 'PM'
 * @returns Hour in 24-hour format (0-23)
 */
export function to24Hour(hour: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') {
    return hour === 12 ? 0 : hour;
  } else {
    return hour === 12 ? 12 : hour + 12;
  }
}

/**
 * Converts 24-hour time to 12-hour format.
 *
 * @param hour24 - Hour in 24-hour format (0-23)
 * @returns Object with hour (1-12) and period ('AM' | 'PM')
 */
export function to12Hour(hour24: number): { hour: number; period: 'AM' | 'PM' } {
  const period: 'AM' | 'PM' = hour24 < 12 ? 'AM' : 'PM';
  let hour = hour24 % 12;
  if (hour === 0) hour = 12;
  return { hour, period };
}

/**
 * Formats time for display (12-hour format with AM/PM).
 *
 * @param hours - Hour in 24-hour format (0-23)
 * @param minutes - Minutes (0-59)
 * @returns Formatted string like "9:30 AM"
 */
export function formatTimeDisplay(hours: number, minutes: number): string {
  const { hour, period } = to12Hour(hours);
  const mins = minutes.toString().padStart(2, '0');
  return `${hour}:${mins} ${period}`;
}

/**
 * Formats time for API (24-hour format HH:MM).
 *
 * @param hours - Hour in 24-hour format (0-23)
 * @param minutes - Minutes (0-59)
 * @returns Formatted string like "09:30"
 */
export function formatTime24(hours: number, minutes: number): string {
  const hrs = hours.toString().padStart(2, '0');
  const mins = minutes.toString().padStart(2, '0');
  return `${hrs}:${mins}`;
}

/**
 * Parses freeform time input into a structured ParsedTime object.
 *
 * Supported formats:
 * - Hour only: "9", "4", "12"
 * - Hour with period: "9am", "4pm", "9a", "4p"
 * - Hour:minute: "9:30", "4:30", "12:00"
 * - Hour:minute with period: "9:30am", "4:30pm", "9:30a", "4:30p"
 * - Compact: "930", "1430", "930am", "430p"
 * - 24-hour: "09:30", "14:30", "0900", "1430"
 *
 * @param input - Raw user input
 * @returns ParsedTime object with all time info
 */
export function parseTimeInput(input: string): ParsedTime {
  // Default invalid result
  const invalid = (error: string): ParsedTime => ({
    hours: 0,
    minutes: 0,
    period: 'AM',
    display: '',
    time24: '',
    valid: false,
    error,
  });

  if (!input || typeof input !== 'string') {
    return invalid('Please enter a time');
  }

  // Normalize: lowercase, trim, remove extra spaces
  const normalized = input.toLowerCase().trim().replace(/\s+/g, '');

  if (!normalized) {
    return invalid('Please enter a time');
  }

  let hours = 0;
  let minutes = 0;
  let explicitPeriod: 'AM' | 'PM' | null = null;

  // Check for explicit AM/PM (a, am, p, pm)
  const periodMatch = normalized.match(/(a|am|p|pm)$/i);
  let timeStr = normalized;

  if (periodMatch) {
    const periodStr = periodMatch[1].toLowerCase();
    explicitPeriod = periodStr.startsWith('a') ? 'AM' : 'PM';
    timeStr = normalized.slice(0, -periodMatch[1].length);
  }

  // Try different patterns

  // Pattern 1: HH:MM or H:MM (with colon)
  const colonMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (colonMatch) {
    hours = parseInt(colonMatch[1], 10);
    minutes = parseInt(colonMatch[2], 10);
  }
  // Pattern 2: HHMM or HMM (4 or 3 digits, no colon)
  else if (/^\d{3,4}$/.test(timeStr)) {
    if (timeStr.length === 4) {
      hours = parseInt(timeStr.slice(0, 2), 10);
      minutes = parseInt(timeStr.slice(2), 10);
    } else {
      // 3 digits: H MM
      hours = parseInt(timeStr.slice(0, 1), 10);
      minutes = parseInt(timeStr.slice(1), 10);
    }
  }
  // Pattern 3: Hour only (1 or 2 digits)
  else if (/^\d{1,2}$/.test(timeStr)) {
    hours = parseInt(timeStr, 10);
    minutes = 0;
  }
  else {
    return invalid('Invalid time format');
  }

  // Validate minutes
  if (minutes < 0 || minutes > 59) {
    return invalid('Minutes must be 0-59');
  }

  // Determine if this is 24-hour format input
  const is24HourInput = hours > 12 || (hours === 0 && !explicitPeriod);

  if (is24HourInput) {
    // Validate 24-hour range
    if (hours > 23) {
      return invalid('Hour must be 0-23');
    }

    // Convert to period for display
    const { hour: hour12, period } = to12Hour(hours);

    // If explicit period was given with 24-hour input, validate it matches
    if (explicitPeriod && explicitPeriod !== period) {
      return invalid(`Invalid: ${hours}:${minutes.toString().padStart(2, '0')} is ${period}, not ${explicitPeriod}`);
    }

    return {
      hours,
      minutes,
      period,
      display: formatTimeDisplay(hours, minutes),
      time24: formatTime24(hours, minutes),
      valid: true,
    };
  }

  // 12-hour format input
  if (hours < 1 || hours > 12) {
    return invalid('Hour must be 1-12');
  }

  // Validate explicit period combinations
  if (explicitPeriod) {
    // Can't have 0am or 0pm
    if (hours === 0) {
      return invalid('Invalid hour');
    }
    // 13am, 14pm etc. are invalid (but we already checked hours <= 12)
  }

  // Determine period: explicit or smart detection
  const period = explicitPeriod || detectPeriod(hours);

  // Convert to 24-hour for storage
  const hours24 = to24Hour(hours, period);

  return {
    hours: hours24,
    minutes,
    period,
    display: formatTimeDisplay(hours24, minutes),
    time24: formatTime24(hours24, minutes),
    valid: true,
  };
}

/**
 * Validates that a time string is in valid HH:MM 24-hour format.
 * Used for validating API-format time strings.
 *
 * @param time24 - Time string in HH:MM format
 * @returns true if valid, false otherwise
 */
export function isValidTime24(time24: string): boolean {
  if (!time24 || typeof time24 !== 'string') return false;

  const match = time24.match(/^(\d{2}):(\d{2})$/);
  if (!match) return false;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

/**
 * Parses a 24-hour time string and returns display format.
 * Useful for converting stored times to display format.
 *
 * @param time24 - Time string in HH:MM format
 * @returns Formatted display string or empty string if invalid
 */
export function time24ToDisplay(time24: string): string {
  if (!isValidTime24(time24)) return '';

  const [hoursStr, minutesStr] = time24.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  return formatTimeDisplay(hours, minutes);
}

/**
 * Steps a 24-hour "HH:MM" time to the next (+1) or previous (-1) quarter hour,
 * clamped to the day (00:00–23:45). Invalid input is returned unchanged.
 */
export function stepQuarterHour(time24: string, direction: 1 | -1): string {
  if (!isValidTime24(time24)) return time24;
  const [h, m] = time24.split(':').map((n) => parseInt(n, 10));
  const total = h * 60 + m;
  let next =
    direction === 1 ? Math.floor(total / 15) * 15 + 15 : Math.ceil(total / 15) * 15 - 15;
  next = Math.max(0, Math.min(23 * 60 + 45, next));
  return formatTime24(Math.floor(next / 60), next % 60);
}

/** Minutes from `start` to `end` (both "HH:MM"); null if either is invalid. */
export function minutesBetweenTimes(start: string, end: string): number | null {
  if (!isValidTime24(start) || !isValidTime24(end)) return null;
  const toMin = (t: string) => {
    const [h, m] = t.split(':').map((n) => parseInt(n, 10));
    return h * 60 + m;
  };
  return toMin(end) - toMin(start);
}
